/**
 * Práce s datumy — UTC kalendářní datum (Prisma @db.Date), ISO "YYYY-MM-DD".
 * Časové zóny: interně pracujeme s UTC midnight,
 * pro display v Europe/Prague pak formátujeme přes toLocaleDateString.
 */

/** Vrátí datum jako "YYYY-MM-DD" v UTC (konzistentní s Prisma Date). */
export function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Začátek týdne (pondělí) v UTC pro daný datum. */
export function startOfWeek(d: Date): Date {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = date.getUTCDay(); // 0=Ne..6=So
  const diff = dow === 0 ? -6 : 1 - dow; // posun na pondělí
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

/** Přičte dny k UTC datumu, vrátí nový Date. */
export function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + days);
  return x;
}

/** Czech short date: "Po 21.4." */
export function czShortDate(d: Date): string {
  const wd = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"][d.getUTCDay()];
  return `${wd} ${d.getUTCDate()}.${d.getUTCMonth() + 1}.`;
}

/** Czech long date: "pondělí 21. dubna 2026" */
export function czLongDate(d: Date): string {
  return new Intl.DateTimeFormat("cs-CZ", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** Vygeneruje 30-min sloty od startHour do endHour (exclusive). */
export function generateTimeSlots(startMin: number, endMin: number): string[] {
  const out: string[] = [];
  for (let t = startMin; t < endMin; t += 30) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  return out;
}

/** První den zobrazeného měsíce (UTC). */
export function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/** Poslední den v měsíci (UTC). */
export function endOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}

/** Přičte N měsíců (kladné i záporné). */
export function addMonths(d: Date, months: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
}

/** Vrátí mřížku 6 × 7 dnů, která pokryje celý měsíc od pondělí. */
export function monthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const dow = first.getUTCDay(); // 0 = neděle, 1 = pondělí…
  const offset = dow === 0 ? -6 : 1 - dow; // jdeme zpět na pondělí
  const start = addDays(first, offset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** "duben 2026" pro hlavičku kalendáře. */
export function czMonthYear(d: Date): string {
  return new Intl.DateTimeFormat("cs-CZ", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}
